import { createClient } from '@supabase/supabase-js';

// Try to get Config from react-native-config for React Native environments
let RNConfig: Record<string, string | undefined> | null = null;
try {
  // Dynamic require to avoid bundling issues on web
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const rnConfig = require('react-native-config');
  RNConfig = rnConfig?.default || rnConfig;
} catch (e) {
  // Not in React Native environment or react-native-config not available
  RNConfig = null;
}

const readEnv = (key: string): string | undefined => {
  // First try react-native-config (for React Native apps)
  if (RNConfig && typeof RNConfig[key] === 'string') {
    return RNConfig[key];
  }

  if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
    return process.env[key];
  }

  const globalEnv = (globalThis as any)?.__CUBEMEET_ENV__;
  if (globalEnv && typeof globalEnv[key] === 'string') {
    return globalEnv[key];
  }

  try {
    const importMeta = Function('return typeof import.meta !== "undefined" ? import.meta : undefined;')();
    const importMetaEnv = importMeta?.env;
    if (importMetaEnv && typeof importMetaEnv[key] === 'string') {
      return importMetaEnv[key];
    }
  } catch (error) {
    // ignore: import.meta may not be supported in this runtime
  }

  return undefined;
};

const resolveEnv = (...candidates: string[]): string | undefined => {
  for (const key of candidates) {
    const value = readEnv(key);
    if (value) {
      return value;
    }
  }
  return undefined;
};

const SUPABASE_URL = resolveEnv('SUPABASE_URL', 'VITE_SUPABASE_URL', 'EXPO_PUBLIC_SUPABASE_URL');
const SUPABASE_ANON_KEY = resolveEnv('SUPABASE_ANON_KEY', 'VITE_SUPABASE_ANON_KEY', 'EXPO_PUBLIC_SUPABASE_ANON_KEY');

console.log('[Supabase] URL resolved:', SUPABASE_URL ? 'YES' : 'NO');
console.log('[Supabase] KEY resolved:', SUPABASE_ANON_KEY ? 'YES' : 'NO');

const createSupabaseClient = () => {
  const createStub = (message: string): any => {
    const error = () => {
      throw new Error(message);
    };

    return new Proxy(error, {
      apply: () => {
        throw new Error(message);
      },
      get: () => createStub(message),
    });
  };

  const stub = createStub('Supabase is not configured for this environment');

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn('[Supabase] Environment variables are not configured.');

    return {
      auth: {
        getSession: async () => ({ data: { session: null }, error: new Error('Supabase is not configured') }),
        getUser: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signInWithPassword: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signUp: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signOut: async () => ({ error: new Error('Supabase is not configured') }),
        resetPasswordForEmail: async () => ({ data: null, error: new Error('Supabase is not configured') }),
        verifyOtp: async () => ({ data: null, error: new Error('Supabase is not configured') }),
        resend: async () => ({ data: null, error: new Error('Supabase is not configured') }),
      },
      from: () => stub,
      rpc: () => stub,
    } as any;
  }

  console.log('[Supabase] Creating client with URL:', SUPABASE_URL);
  
  try {
    const client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        // Persist session on web so browsers keep the login across navigations/refresh.
        // We keep `detectSessionInUrl: false` to avoid URL hash session handling issues.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
      global: {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
      db: {
        schema: 'public',
      },
    });

    console.log('[Supabase] Client created successfully!');
    return client;
  } catch (err) {
    console.error('[Supabase] Failed to create client:', err);
    return {
      auth: {
        getSession: async () => ({ data: { session: null }, error: new Error('Supabase is not configured') }),
        getUser: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signInWithPassword: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signUp: async () => ({ data: { user: null }, error: new Error('Supabase is not configured') }),
        signOut: async () => ({ error: new Error('Supabase is not configured') }),
        resetPasswordForEmail: async () => ({ data: null, error: new Error('Supabase is not configured') }),
        verifyOtp: async () => ({ data: null, error: new Error('Supabase is not configured') }),
        resend: async () => ({ data: null, error: new Error('Supabase is not configured') }),
      },
      from: () => stub,
      rpc: () => stub,
    } as any;
  }
};

export const supabase = createSupabaseClient();

export type { SupabaseClient } from '@supabase/supabase-js';
